/**
 * Firestore helpers for the Business Context Library.
 * Collection: artifacts/{appId}/businessContextLibrary
 *
 * Items are reusable snippets about GSGO (mission, programs, differentiators, etc.)
 * that are injected into the AI system prompt when generating blog articles.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db, appId } from './firebase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BusinessContextItem {
  id: string;
  title: string;
  content: string;
  order: number;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy?: string | null;
}

export interface BusinessContextItemPayload {
  title: string;
  content: string;
  order?: number;
}

// ---------------------------------------------------------------------------
// Ref helpers
// ---------------------------------------------------------------------------

const contextRef = () =>
  collection(db, 'artifacts', appId, 'businessContextLibrary');

const contextDocRef = (id: string) =>
  doc(db, 'artifacts', appId, 'businessContextLibrary', id);

function toItem(id: string, data: DocumentData): BusinessContextItem {
  return {
    id,
    title: data.title ?? '',
    content: data.content ?? '',
    order: data.order ?? 0,
    createdAt: data.createdAt ?? new Date(),
    updatedAt: data.updatedAt ?? new Date(),
    createdBy: data.createdBy ?? null,
  };
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

/** List all business context items ordered by `order` asc. */
export async function getAllBusinessContextItems(): Promise<BusinessContextItem[]> {
  const q = query(contextRef(), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toItem(d.id, d.data()));
}

/** Create a new business context item. Returns the new document id. */
export async function createBusinessContextItem(
  payload: BusinessContextItemPayload,
  createdBy: string
): Promise<string> {
  const docRefResult = await addDoc(contextRef(), {
    ...payload,
    order: payload.order ?? 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy,
  });
  return docRefResult.id;
}

/** Update an existing business context item. */
export async function updateBusinessContextItem(
  itemId: string,
  payload: Partial<BusinessContextItemPayload>,
  updatedBy: string
): Promise<void> {
  await setDoc(
    contextDocRef(itemId),
    { ...payload, updatedAt: serverTimestamp(), updatedBy },
    { merge: true }
  );
}

/** Delete a business context item. */
export async function deleteBusinessContextItem(itemId: string): Promise<void> {
  await deleteDoc(contextDocRef(itemId));
}

/** Batch-update `order` for a list of ids (used after drag-to-reorder or arrow reorder). */
export async function reorderBusinessContextItems(
  orderedIds: string[],
  updatedBy: string
): Promise<void> {
  await Promise.all(
    orderedIds.map((id, index) =>
      setDoc(
        contextDocRef(id),
        { order: index, updatedAt: serverTimestamp(), updatedBy },
        { merge: true }
      )
    )
  );
}

/**
 * Concatenate all business context items into a single plain-text block
 * suitable for injecting into an AI system or user prompt.
 */
export function buildBusinessContextText(items: BusinessContextItem[]): string {
  if (items.length === 0) return '';
  return items
    .map((item) => `## ${item.title}\n${item.content}`)
    .join('\n\n');
}

/**
 * Build a short business summary from context items for use in "Make me lucky"
 * and other prompts where a brief business blurb is needed. Truncates to
 * maxChars (default 600) so the pre-fill stays readable.
 */
export function buildShortBusinessSummary(
  items: BusinessContextItem[],
  maxChars: number = 600
): string {
  if (items.length === 0) return '';
  const full = items
    .map((item) => `${item.title}: ${item.content.replace(/\n/g, ' ').trim()}`)
    .join(' ');
  const trimmed = full.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= maxChars) return trimmed;
  const cut = trimmed.slice(0, maxChars - 1).trim();
  const lastSpace = cut.lastIndexOf(' ');
  const end = lastSpace > maxChars * 0.6 ? lastSpace : cut.length;
  return cut.slice(0, end) + '…';
}

// ---------------------------------------------------------------------------
// Default GSGO context (seed from admin UI — no service account needed)
// ---------------------------------------------------------------------------

export const DEFAULT_GSGO_CONTEXT_ITEMS: Array<{
  id: string;
  title: string;
  content: string;
  order: number;
}> = [
  {
    id: 'mission',
    title: 'Mission Statement',
    content: `GSGO is a nonprofit support organization for patients and families impacted by chronic kidney disease (CKD). Our mission is to provide practical education, compassionate support, advocacy, and community-driven programs that improve quality of life and long-term wellness outcomes.`,
    order: 0,
  },
  {
    id: 'key-services',
    title: 'Key Services',
    content: `GSGO offers:
- Patient and caregiver education resources
- Community support programming and outreach
- Advocacy and awareness initiatives
- Family-centered navigation support
- Volunteer and donor engagement programs
- Partner collaboration for scalable impact`,
    order: 1,
  },
  {
    id: 'differentiators',
    title: 'Differentiators',
    content: `What sets GSGO apart:
- Deep focus on CKD patients and family needs
- Practical, actionable guidance with clear next steps
- Community-first delivery model built on trust
- Strong alignment between education, advocacy, and measurable impact
- Collaboration with partners to extend reach and outcomes`,
    order: 2,
  },
  {
    id: 'target-audience',
    title: 'Target Audience',
    content: `GSGO serves:
- CKD patients and families seeking trusted support
- Caregivers who need practical guidance and resources
- Community members who want to contribute through volunteering and advocacy
- Donors and partners focused on measurable social impact`,
    order: 3,
  },
  {
    id: 'tone-and-voice',
    title: 'Tone & Voice Guidelines',
    content: `When writing for GSGO:
- Compassionate and trustworthy: we are mission-driven and people-first
- Practical and actionable: give readers clear next steps and specific guidance where possible
- Never salesy or hyperbolic: avoid vague marketing language and overpromising
- Use real CKD support concepts and terms relevant to patients and caregivers
- Be concise; use headings and lists for scannability. Prefer concrete examples over abstract claims.`,
    order: 4,
  },
  {
    id: 'insights-article-standard',
    title: 'Insights Article Standard',
    content: `Blog articles on the Insights page must match this standard:

- Positioning: "Practical insights for CKD patients, caregivers, and supporters."

- Topics we cover: patient support, caregiver guidance, community resources, advocacy, fundraising impact, and partnership stories.

- Every article must include: (1) Practical Insights — clear guidance readers can apply immediately; (2) Impact Focus — sustainable, meaningful support outcomes; (3) Clear Next Steps — every article ends with a direct path forward (e.g. get help, donate, volunteer, or partner).`,
    order: 5,
  },
  {
    id: 'govcon-basics',
    title: 'CKD Support Basics (for accuracy)',
    content: `Use these accurately in content:
- CKD: chronic kidney disease and its stages
- Patient and caregiver support resources and pathways
- Advocacy and awareness initiatives
- Community fundraising and impact reporting
- Practical care-navigation and support guidance`,
    order: 6,
  },
];

/**
 * Seed or update the default GSGO context items in Firestore.
 * Call from the admin UI (no service account needed). Idempotent: re-running merges into existing docs.
 */
export async function seedDefaultGSGOContext(createdBy: string): Promise<void> {
  const ts = serverTimestamp();
  for (const item of DEFAULT_GSGO_CONTEXT_ITEMS) {
    await setDoc(
      contextDocRef(item.id),
      {
        title: item.title,
        content: item.content,
        order: item.order,
        createdAt: ts,
        updatedAt: ts,
        createdBy,
      },
      { merge: true }
    );
  }
}
